import { FocusEventHandler, useEffect, useMemo, useRef, useState } from "react";
import styles from "./prizeTicketSelector.module.css";
import Button from "../Button/button";
import { addTickets, subscriptionTicketsEnterNow } from "../../common-service";
import { useAppDispatch } from "../../redux/hooks";
import { publishModalVisibility } from "../../redux/features/modal/modalSlice";
import { AddTicketsData, BuyTicketPopupProps } from "../../common";
// import {
//   publishCartData,
//   publishCartState,
// } from "../../redux/features/cart/cartSlice";
import { toast } from "react-toastify";
import ToastNotification from "../ToastNotification/toastNotification";
import QuickSelectSlider from "./quickSelectSlider";
import QuickSelectBoxes from "./QuickSelectBoxes";
import PlusIcon from "../../shared-components/icons/PlusIcon";
import MinusIcon from "../../shared-components/icons/MinusIcon";
import ChevronsRightIcon from "../../shared-components/icons/ChevronsRightIcon";
import { sendCustomEvent } from "../../../gtm/functions";
import { CURRENCY_MAPPING } from "../../app-constants";

const BuyTicket = ({
  saleTicketPrice,
  baseTicketPrice,
  carImageSource,
  make,
  model,
  currencySymbol,
  discountPercentage,
  saleTicketPriceWithoutCurrency,
  overrideDiscount,
  maxTicketsPerUser,
  baseTicketPriceWithoutCurrency,
  competitionId,
  prizeId,
  quickBuySelections,
  defaultTicketCount,
  showInstantWinText,
  multibuyLowerPercent,
  multibuyLowerQuantity,
  multibuyUpperPercent,
  multibuyUpperQuantity,
  subscriptionTickets,
  useSubscriptionTicketsMode = false,
  onPopupCallback,
  onAddMoreTicketsCallback,
  SelectorMode = "selectorButtons",
  PriceWithoutCurrency,
  competitionTypePrefix,
  prizeBoostEndDate,
  prizeBoostImageUrl,
  prizeBoostLowerQuantity,
  prizeBoostLowerValue,
  prizeBoostUpperQuantity,
  prizeBoostUpperValue,
  prizeBoostPromoText,
  onAddTicketCallback,
  addToCartMethodId,
  hasPassTickets = false,
}: BuyTicketPopupProps) => {
  const dispatch = useAppDispatch();
  // When the modal is opened on the "BOTB Pass entry" tab we enter subscription mode:
  // the quantity defaults to the number of available pass tickets and is capped to it.
  const initialQuantity = useSubscriptionTicketsMode ? subscriptionTickets.toString() : defaultTicketCount.toString();
  const [quantity, setQuantity] = useState<string>(initialQuantity);
  const intervalRef = useRef<NodeJS.Timeout>(null);
  const isIntervalRunning = useRef<boolean>(false);
  const isIntervalStopped = useRef<boolean>(false);
  const quantityRef = useRef<number>(Number(initialQuantity));
  const maxTicketsPerUserRef = useRef<number>(useSubscriptionTicketsMode ? subscriptionTickets : maxTicketsPerUser);
  const subscriptionTicketsRef = useRef<number>(subscriptionTickets);
  const isUsingSubscriptionTickets = useSubscriptionTicketsMode;
  const [showSubscriptionEnterConfirmaiton, setShowSubscriptionEnterConfirmaiton] = useState(false);
  const [subscriptionTicketEnteredCompetitionName, setSubscriptionTicketEnteredCompetitionName] = useState(null);
  const [subscriptionTicketsEnteredCount, setSubscriptionTicketsEnteredCount] = useState(0);
  const [subscriptionTicketsButtonUrl, setSubscriptionTicketsButtonUrl] = useState(null);
  const [isEnterNowInFlight, setIsEnterNowInFlight] = useState(false);
  const isEnterNowInFlightRef = useRef(false);
  const quantityInputRef = useRef<HTMLInputElement>(null);

  // Prize booster variables
  const shouldShowPrizeBoostInfo =
    ((prizeBoostLowerQuantity !== null &&
      prizeBoostLowerQuantity > 0 &&
      prizeBoostLowerValue !== null &&
      prizeBoostLowerValue > 0) ||
      (prizeBoostUpperQuantity !== null &&
        prizeBoostUpperQuantity > 0 &&
        prizeBoostUpperValue !== null &&
        prizeBoostUpperValue > 0)) &&
    (prizeBoostEndDate === null || prizeBoostEndDate > Math.floor(Date.now() / 1000));

  useEffect(() => {
    return () => {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      isIntervalStopped.current = false;
      isIntervalRunning.current = false;
    };
  }, []);

  const incrementQuantity = (event: React.MouseEvent<HTMLButtonElement>, isUserIncrementing: boolean) => {
    event.preventDefault();
    event.stopPropagation();
    isIntervalStopped.current = false;
    // Subscription (BOTB Pass) tab only: enforce bounds [1, available tickets] regardless of
    // the button's disabled state, which can lag after switching tabs.
    if (isUsingSubscriptionTickets) {
      if (isUserIncrementing && quantityRef.current >= maxTicketsPerUserRef.current) {
        return;
      }
      if (!isUserIncrementing && quantityRef.current <= 1) {
        return;
      }
    }
    // If user is clicking on the button very fast then we need to stop creating any more intervals as this could
    // lead to see the quantity increasing very fast - happens when asynchronous interval callbacks are not cleared
    // and showing jumping effect in the values
    if (intervalRef.current) {
      return;
    }
    intervalRef.current = setInterval(() => {
      isIntervalRunning.current = true;
      if (isUserIncrementing) {
        quantityRef.current++;
      } else {
        quantityRef.current--;
      }
      quantityRef.current = Number(validateUserInputQuantity(quantityRef.current));
      if (quantityRef.current > maxTicketsPerUserRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        isIntervalStopped.current = false;
        isIntervalRunning.current = false;
        quantityRef.current = maxTicketsPerUserRef.current;
        return;
      }
      if (isUsingSubscriptionTickets && quantityRef.current < 1) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        isIntervalStopped.current = false;
        isIntervalRunning.current = false;
        quantityRef.current = 1;
        setQuantity(quantityRef.current.toString());
        if (quantityInputRef.current) {
          quantityInputRef.current.value = quantityRef.current.toString();
        }
        return;
      }
      console.log("interval running for value - " + quantityRef.current);
      setQuantity(quantityRef.current.toString());
      quantityInputRef.current.value = quantityRef.current.toString();
      // clear the interval now after the callback has run for atleast once
      if (isIntervalStopped.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        isIntervalStopped.current = false;
        isIntervalRunning.current = false;
      }
    }, 200); // 200ms is the calculated time that is not triggering the interval callback twice on the tap on the button.
    // If this time does not work, then we need a new reference to check whether user is doing long press or just a tap.
    // For now, 200ms is working fine
  };

  // This is being fired late in case of tapping on the button - so, interval callback is getting fired twice
  const onStopCounter = () => {
    isIntervalStopped.current = true;
    // Only clear the interval if it's callback has run for atleast once. If you clear before the callback runs,
    // then you will not see the updated value
    if (intervalRef.current && isIntervalRunning.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      isIntervalStopped.current = false;
      isIntervalRunning.current = false;
    }
  };

  const onQuantityChanged = (newQuantity: number) => {
    quantityRef.current = newQuantity;
    setQuantity(quantityRef.current.toString());
    quantityInputRef.current.value = String(newQuantity);
  };

  const addQuickSelect = (event: React.MouseEvent<HTMLButtonElement>, quantity: number) => {
    event.stopPropagation();
    onQuantityChanged(quantity);
  };

  const activePrice = saleTicketPriceWithoutCurrency ? saleTicketPriceWithoutCurrency : baseTicketPriceWithoutCurrency;
  const showBasePrizeAlongWithSalePrize = activePrice !== baseTicketPriceWithoutCurrency;
  const activePizeClass = showBasePrizeAlongWithSalePrize ? "text-[#FF8200]" : "text-[#28282B] pr-4";

  const validateUserInputQuantity = (quantity: number) => {
    if (isNaN(quantity)) {
      return defaultTicketCount.toString();
    } else {
      if (quantity > maxTicketsPerUser) {
        return maxTicketsPerUser.toString();
      } else if (quantity < 1) {
        return "";
      } else {
        return Math.ceil(Number(quantity)).toString();
      }
    }
  };

  const onQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // BOTB Pass tab: keep the input editable, but if the typed value is below 1 or above
    // the number of available pass tickets, snap it back to 1.
    if (isUsingSubscriptionTickets) {
      const rawValue = event.target.value;
      // Allow an empty field while the user is mid-edit; onBlur restores the last value.
      if (rawValue === "") {
        return;
      }
      const maxPassTickets = subscriptionTicketsRef.current;
      let nextQuantity = Math.ceil(Number(rawValue));
      if (isNaN(nextQuantity) || nextQuantity < 1 || nextQuantity > maxPassTickets) {
        nextQuantity = 1;
      }
      event.target.value = nextQuantity.toString();
      quantityRef.current = nextQuantity;
      setQuantity(nextQuantity.toString());
      return;
    }
    const updatedQuantity = validateUserInputQuantity(Number(event.target.value));
    if (updatedQuantity === "") {
      event.target.value = String(defaultTicketCount);
      setQuantity(quantityRef.current.toString());
      return;
    }
    event.target.value = updatedQuantity;
    quantityRef.current = Number(updatedQuantity);
    setQuantity(quantityRef.current.toString());
  };

  const onInputFocus: FocusEventHandler<HTMLInputElement> = event => {
    event.target.value = "";
  };
  const onInputBlur: FocusEventHandler<HTMLInputElement> = event => {
    event.target.value = quantity;
  };

  const handleCartAndNotificationScenario = (data: AddTicketsData) => {
    let skipShowCart = false;

    switch (data.AddTicketStatusResult) {
      case 1:
      case 5:
        const addTicketStatusResult = data.AddTicketStatusResult;
        const ticketsAdded = data.TicketsAdded;
        let message = "{number_of_tickets} added to your basket";
        if (addTicketStatusResult === 1) {
          if (ticketsAdded === 1) {
            message = message.replace("{number_of_tickets}", ticketsAdded + " ticket");
          } else if (ticketsAdded > 1) {
            message = message.replace("{number_of_tickets}", ticketsAdded + " tickets");
          }
        } else {
          const ticketLimit = data.TicketLimit;
          const requestedTicketCount = data.TicketsRequested;
          message = `Adding ${requestedTicketCount} ticket${requestedTicketCount > 1 ? "s" : ""} to your basket would exceed the
 ticket limit (${ticketLimit}) for this Competition. ${ticketsAdded} ticket${ticketsAdded > 1 ? "s have" : " has"}
 therefore been added, leaving you with ${ticketLimit} tickets in this Competition.`;
        }

        const skipNotification = addTicketStatusResult === 1;
        if (!skipNotification) {
          toast.info(<ToastNotification message={message} />);
        }

        // Log to GTM
        if (typeof window.dataLayer !== "undefined") {
          window.dataLayer.push({
            event: "ticketAdded",
            promo_id: 6,
            product_id: data.PrizeName,
          });
        }

        // refresh STB
        if (window.botb?.spotTheBall?.controller) {
          window.botb.spotTheBall.controller.reloadBasket();
        }
        break;
      case 2: // Limit exceeded
        toast.error(
          <ToastNotification
            message={`You have reached the maximum permitted quantity of ${data.TicketLimit} tickets for this competition. For the avoidance of doubt, each individual is limited to ${data.TicketLimit} tickets and you may not exceed this number by using additional accounts. Abuse of this limit will lead to disqualification of all entries without compensation.`}
          />
        );
        skipShowCart = true;
        break;
      case 3: // loyalty level too low
        toast.error(<ToastNotification message="Your loyalty level is too low to add tickets for this prize." />);
        break;
      case 4: // General Error - please refresh
        toast.error(
          <ToastNotification message="We have been unable to add tickets to your basket. Please reload the page and try again." />
        );
        break;
    }

    if (!skipShowCart) {
      // dispatch(publishCartData({ cartData: data.Cart }));
      // dispatch(publishCartState({ isOpen: true }));
      const addTicketData = JSON.stringify(data);

      // We still need to use native javascript dispatch event because shoppingCart.tsx and buyTicket.tsx
      // are not under the same redux store state in MVC environment. Once we are fully migrate to React, they will be
      // under one redux store state tree
      dispatchEvent(
        new CustomEvent("addToBasketApiEvent", {
          detail: { addToBasketApiData: addTicketData, prizeId },
        })
      );
    }
  };

  const addTicketsToBasket = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    try {
      const res = await addTickets(competitionId, prizeId, parseInt(quantity), addToCartMethodId ?? 1);
      const data = (await res.json()) as AddTicketsData;
      const trkData = data.TrackingData;
      if (trkData?.tiktok) {
        sendCustomEvent(trkData.tiktok);
      }
      dispatch(publishModalVisibility({ isOpen: false }));
      const isReactHeaderEnabled = document.getElementById("user-shopping-cart");
      if (isReactHeaderEnabled) {
        handleCartAndNotificationScenario(data);
      } else {
        // interact with Angular cart
        const addTicketData = JSON.stringify(data);
        dispatchEvent(
          new CustomEvent("addToBasketApiEvent", {
            detail: { addToBasketApiData: addTicketData, prizeId },
          })
        );
      }

      if (onAddTicketCallback) {
        onAddTicketCallback(data);
      }
    } catch (err) {
      console.error(`Error while adding ticket to the basket = ${JSON.stringify(err)}`);
      toast.error(
        <ToastNotification message="We have been unable to add another ticket to your basket, please try again or <a href='/contact-us'>get in touch.</a>" />
      );
    }
  };

  const handleSubscriptionTicketsEnterNow = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (isEnterNowInFlightRef.current) {
      return;
    }
    isEnterNowInFlightRef.current = true;
    setIsEnterNowInFlight(true);

    const enteredCount = parseInt(quantity) || 1;
    try {
      const res = await subscriptionTicketsEnterNow(competitionId, prizeId, enteredCount);
      const response = await res.json();

      if (response !== null && res.ok) {
        setSubscriptionTicketsEnteredCount(enteredCount);
        setSubscriptionTicketEnteredCompetitionName(response.CompetitionName);
        subscriptionTicketsRef.current = response.RemainingNumberOfTickets;
        if (showInstantWinText) {
          setSubscriptionTicketsButtonUrl(
            response.LongOrderId ? `/account/instant-win/results?order=${response.LongOrderId}` : "/account/instant-win"
          );
        } else {
          setSubscriptionTicketsButtonUrl("/");
        }
        setShowSubscriptionEnterConfirmaiton(true);

        if (onPopupCallback) {
          onPopupCallback();
        }
      } else {
        isEnterNowInFlightRef.current = false;
        setIsEnterNowInFlight(false);
        toast.error(<ToastNotification message={`An error occurred. Please try again later`} />);
      }
    } catch (err) {
      isEnterNowInFlightRef.current = false;
      setIsEnterNowInFlight(false);
      console.error(`Error while executing subscriptionTicketsEnterNow = ${JSON.stringify(err)}`);
      toast.error(
        <ToastNotification message="We have been unable to use your subscription tickets, please try again or <a href='/contact-us'>get in touch.</a>" />
      );
    }
  };

  const goToFeaturedCompetitions = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    dispatch(publishModalVisibility({ isOpen: false }));
    // Featured Competitions is a homepage category section. Match the header behaviour:
    // navigate to the homepage anchored on "featured" when we're elsewhere, otherwise
    // dispatch the scroll-to-category event the homepage listens for.
    if (location.pathname !== "/") {
      location.href = "/?navigateTo=featured";
      return;
    }
    window.dispatchEvent(new CustomEvent("scroll-to-category", { detail: "featured" }));
  };

  const handleAddMoreTickets = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    // Reset quantity back to the remaining pass tickets and return to the entry view.
    const remaining = Math.max(subscriptionTicketsRef.current, 1);
    maxTicketsPerUserRef.current = subscriptionTicketsRef.current;
    quantityRef.current = remaining;
    setQuantity(remaining.toString());
    if (quantityInputRef.current) {
      quantityInputRef.current.value = remaining.toString();
    }
    isEnterNowInFlightRef.current = false;
    setIsEnterNowInFlight(false);
    setShowSubscriptionEnterConfirmaiton(false);
    if (onAddMoreTicketsCallback) {
      onAddMoreTicketsCallback(subscriptionTicketsRef.current);
    }
  };

  const getDiscountPercentValue = () => {
    if (discountPercentage > 0) {
      if (overrideDiscount) {
        return overrideDiscount;
      }
      return discountPercentage;
    }
  };

  const numberFormatter = new Intl.NumberFormat("en-GB", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    currency: CURRENCY_MAPPING[currencySymbol] || "GBP",
    style: "currency",
  });
  const numberFormatterWithoutCurrency = new Intl.NumberFormat("en-GB", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    style: "decimal",
  });
  const multiBuyOffers = useMemo(() => {
    const offers: { qs: number; percent: number }[] = [];
    for (const qs of quickBuySelections) {
      if (multibuyLowerQuantity == qs && multibuyLowerPercent > 0) {
        offers.push({ qs, percent: multibuyLowerPercent });
        continue;
      }
      if (multibuyUpperQuantity == qs && multibuyUpperPercent > 0) {
        offers.push({ qs, percent: multibuyUpperPercent });
        continue;
      }
      if (multibuyLowerQuantity == qs && multibuyLowerPercent > 0) {
        offers.push({ qs, percent: multibuyLowerPercent });
      }
    }
    return offers;
  }, [quickBuySelections, multibuyLowerPercent, multibuyUpperPercent, multibuyUpperQuantity, multibuyLowerQuantity]);
  const activeSegmentIndex = useMemo(() => {
    return multiBuyOffers.findIndex((offer, index) => {
      if (index + 1 >= multiBuyOffers.length) {
        return offer.qs <= Number(quantity);
      }
      return offer.qs <= Number(quantity) && multiBuyOffers[index + 1]?.qs > Number(quantity);
    });
  }, [multiBuyOffers, quantity]);
  const total = useMemo(() => {
    const _activePrice = Number(PriceWithoutCurrency);
    const _quantity = Number(quantity);
    return _quantity * _activePrice;
  }, [quantity, PriceWithoutCurrency]);
  const savingsFromTotal = useMemo<number>(() => {
    if (activeSegmentIndex !== -1) {
      const offer = multiBuyOffers[activeSegmentIndex];
      return total * offer.percent;
    }
    return 0;
  }, [activeSegmentIndex, total]);
  // When paying with BOTB Pass tickets the entry is free, so the amount due is 0 and the
  // whole ticket price is shown as the saving.
  const amountDue = isUsingSubscriptionTickets ? 0 : total - savingsFromTotal;
  const amountSaved = isUsingSubscriptionTickets ? total : savingsFromTotal;

  const parsePromoTemplate = (promoTemplate: string, replacements: Record<string, string>) => {
    const template = promoTemplate.replaceAll(
      /<<([^>]+)>>/g,
      (_, match) => `<strong class="text-primary">${match}</strong>`
    );

    return template.replaceAll(/{{([^}]+)}}/g, (_, key) => {
      return replacements[key] || `{{${key}}}`;
    });
  };

  return (
    <>
      <div
        className={`overflow-y-auto max-lg:max-h-[60vh] max-lg:[@media(min-height:480px)]:max-h-[80vh] ${showInstantWinText ? "max-lg:[@media(min-height:520px)]:min-h-[394px] lg:min-h-[432px]" : "max-lg:[@media(min-height:520px)]:min-h-[358px] lg:min-h-[396px]"} ${showSubscriptionEnterConfirmaiton ? "hidden" : ""}`}>
        <div className="flex gap-3 items-center min-h-[60px] lg:min-h-[90px]">
          <div className="relative w-[79px] lg:w-[148px] h-[48px] lg:h-[90px] shrink-0">
            <img src={carImageSource} alt={`buy-${make}-${model}`} className="size-full object-contain object-center" />
          </div>
          <div className="grow flex flex-col gap-1 lg:gap-3">
            <h2
              className={
                "m-0 text-left text-[#28282B] font-jost text-xs font-medium leading-4 lg:font-semibold whitespace-normal"
              }>
              {make} {model}
            </h2>

            {isUsingSubscriptionTickets && (
              <div className="min-h-[24px] lg:min-h-[30px] text-dark-1 text-[20px] font-bold leading-[24px] lg:text-[24px] lg:leading-[30px] font-jost">
                Free entry
              </div>
            )}

            {!isUsingSubscriptionTickets && (
              <div className="flex items-center">
                <div className={`flex min-h-[24px] lg:min-h-[30px] ${activePizeClass}`}>
                  <div className="inline-flex items-baseline text-dark-1 text-[20px] font-bold lg:text-[24px] leading-[24px] lg:leading-[30px] font-jost">
                    <span className="text-[16px] lg:text-[18px] leading-none">{currencySymbol}</span>
                    {activePrice}
                  </div>
                </div>

                {showBasePrizeAlongWithSalePrize && (
                  <span className={`text-[1rem] leading-none px-4 ${saleTicketPrice ? "line-through" : ""}`}>
                    {baseTicketPrice}
                  </span>
                )}
                {discountPercentage > 0 && (
                  <span className="text-[1rem] text-[#FF8200] leading-none">{`${getDiscountPercentValue()}% off`}</span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="quantitySection mt-4 mb-6 min-h-[182px] lg:min-h-[186px]">
          <p className={`${styles.ticketQuantity}`}>Quantity</p>
          <div className="flex flex-col gap-4 mt-4">
            <div className="flex items-center lg:justify-between w-full gap-3">
              <input
                ref={quantityInputRef}
                type="number"
                inputMode={"numeric"}
                min="0"
                max={maxTicketsPerUserRef.current}
                className="w-[61px] h-[37px] shrink-0 rounded-[4px] !border-[1.5px] !border-black/16 !border-solid outline-none text-dark-1 text-center text-sm font-semibold leading-5"
                defaultValue={quantity}
                onClick={event => event.stopPropagation()}
                onFocus={onInputFocus}
                onBlur={onInputBlur}
                onChange={onQuantityChange}
              />

              <div className={"grow lg:grow-0 lg:w-[157px] lg:shrink-0"}>
                <QuickSelectSlider
                  quantityInputRef={quantityInputRef}
                  onQuantityChanged={onQuantityChanged}
                  maxTicketsPerUser={maxTicketsPerUserRef.current}
                  quantity={quantity}
                  subscriptionMode={isUsingSubscriptionTickets}
                />
              </div>

              <div className={"flex items-center gap-3 shrink-0"}>
                <Button
                  className={`flex items-center justify-center w-[39px] h-[37px] rounded-[4px] !border-[1.5px] !border-solid border-black/16 bg-white hover:bg-primary hover:border-primary text-secondary hover:text-white outline-none select-none touch-none disabled:!opacity-60 ${styles.ticketCountUpdateButton}`}
                  onPointerDown={e => incrementQuantity(e, false)}
                  onPointerUp={onStopCounter}
                  onPointerLeave={onStopCounter}
                  onPointerCancel={onStopCounter}
                  isDisabled={isUsingSubscriptionTickets ? Number(quantity) <= 1 : quantity === "0"}
                  text="">
                  <MinusIcon className={"size-4 shrink-0"} />
                </Button>
                <Button
                  className={`flex items-center justify-center w-[39px] h-[37px] rounded-[4px] !border-[1.5px] !border-solid border-black/16 bg-white hover:bg-primary hover:border-primary text-secondary hover:text-white outline-none select-none touch-none disabled:!opacity-60 ${styles.ticketCountUpdateButton}`}
                  onPointerDown={e => incrementQuantity(e, true)}
                  onPointerUp={onStopCounter}
                  onPointerLeave={onStopCounter}
                  onPointerCancel={onStopCounter}
                  text=""
                  isDisabled={
                    isUsingSubscriptionTickets
                      ? quantity === subscriptionTicketsRef.current.toString()
                      : quantity === maxTicketsPerUser.toString()
                  }>
                  <PlusIcon className={"size-4 shrink-0"} />
                </Button>
              </div>
            </div>

            {isUsingSubscriptionTickets && (
              <div className="flex items-center gap-1.5 pb-4 px-2 border-b border-solid border-black/8">
                <svg
                  className="shrink-0"
                  width="21"
                  height="16"
                  viewBox="0 0 21 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M20.8 5.7472V1.6C20.8 0.716 20.084 0 19.2 0H1.6C0.716 0 0 0.716 0 1.6V5.7472C0.9304 6.0776 1.6 6.9568 1.6 8C1.6 9.0432 0.9304 9.9224 0 10.2528V14.4C0 15.284 0.716 16 1.6 16H19.2C20.084 16 20.8 15.284 20.8 14.4V10.2528C19.8696 9.9224 19.2 9.0432 19.2 8C19.2 6.9568 19.8696 6.0776 20.8 5.7472ZM14.8416 7.1688L12.52 8.8968L13.3824 11.6592C13.4984 12.0296 13.0776 12.3352 12.7608 12.1104L10.4 10.4368L8.0392 12.1104C7.7224 12.3352 7.3024 12.0296 7.4176 11.6592L8.28 8.8968L5.9584 7.1688C5.6472 6.9376 5.808 6.4432 6.196 6.4384L9.0896 6.4048L10.016 3.6632C10.14 3.296 10.66 3.296 10.784 3.6632L11.7104 6.4048L14.604 6.4384C14.992 6.4424 15.1528 6.9368 14.8416 7.1688Z"
                    fill="#FF8200"></path>
                </svg>

                <p className="text-[#696971] font-jost text-xs font-medium leading-4">
                  {Number(quantity) >= subscriptionTicketsRef.current ? (
                    <>
                      You have <span className="font-bold">{subscriptionTicketsRef.current}</span>{" "}
                      {subscriptionTicketsRef.current <= 1 ? "BOTB Pass Ticket" : "BOTB Pass Tickets"}
                    </>
                  ) : (
                    <>
                      Playing <span className="font-bold">{Number(quantity)}</span> of{" "}
                      <span className="font-bold">{subscriptionTicketsRef.current}</span> BOTB Pass Tickets
                    </>
                  )}
                </p>
              </div>
            )}

            <QuickSelectBoxes
              quickBuySelections={quickBuySelections}
              addQuickSelect={addQuickSelect}
              multibuyUpperQuantity={multibuyUpperQuantity}
              multibuyLowerQuantity={multibuyLowerQuantity}
              multibuyLowerPercent={multibuyLowerPercent}
              isUsingSubscriptionTickets={isUsingSubscriptionTickets}
              maxTicketsPerUser={maxTicketsPerUser}
              quantity={quantity}
              multibuyUpperPercent={multibuyUpperPercent}
            />
          </div>
        </div>

        {shouldShowPrizeBoostInfo && (
          <div className={"flex w-full justify-between items-end mt-6 mb-6"}>
            <div className={"flex flex-col items-start gap-2 shrink-0"}>
              <div
                className={
                  "self-stretch text-secondary font-jost text-sm font-medium leading-5 lg:text-base lg:leading-6"
                }>
                Boost your prize for extra cash!
              </div>
              <div className={"flex flex-col items-start gap-1 lg:gap-2 self-stretch"}>
                {prizeBoostLowerQuantity !== null &&
                prizeBoostLowerQuantity > 0 &&
                prizeBoostLowerValue !== null &&
                prizeBoostLowerValue > 0 ? (
                  <div className={"flex items-center gap-2"}>
                    <img
                      src={"/assets/images/booster/tabler-icon-rocket.svg"}
                      alt="boost prize"
                      className="size-4 lg:size-5 shrink-0"
                      width={16}
                      height={16}
                    />
                    <div
                      className={
                        "shrink-0 text-gray-5 font-jost text-xs font-medium leading-3 lg:text-[13px] lg:leading-[18px]"
                      }
                      dangerouslySetInnerHTML={{
                        __html: parsePromoTemplate(prizeBoostPromoText, {
                          value: numberFormatterWithoutCurrency.format(prizeBoostLowerValue),
                          quantity: numberFormatterWithoutCurrency.format(prizeBoostLowerQuantity),
                        }),
                      }}
                    />
                  </div>
                ) : null}
                {prizeBoostUpperQuantity !== null &&
                prizeBoostUpperQuantity > 0 &&
                prizeBoostUpperValue !== null &&
                prizeBoostUpperValue > 0 ? (
                  <div className={"flex items-center gap-2"}>
                    <img
                      src={"/assets/images/booster/tabler-icon-rocket.svg"}
                      alt="boost prize"
                      className="size-4 lg:size-5 shrink-0"
                      width={16}
                      height={16}
                    />
                    <div
                      className={
                        "shrink-0 text-gray-5 font-jost text-xs font-medium leading-3 lg:text-[13px] lg:leading-[18px]"
                      }
                      dangerouslySetInnerHTML={{
                        __html: parsePromoTemplate(prizeBoostPromoText, {
                          value: numberFormatterWithoutCurrency.format(prizeBoostUpperValue),
                          quantity: numberFormatterWithoutCurrency.format(prizeBoostUpperQuantity),
                        }),
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </div>
            <img
              src={prizeBoostImageUrl || "/assets/images/booster/boost-modal.png"}
              alt="boost prize"
              className="w-[65px] lg:w-[87px] h-auto shrink-0"
              width={65}
              height={40}
            />
          </div>
        )}

        {showInstantWinText && (
          <p className="text-center mt-6 mb-2 font-jost text-xs leading-normal text-[#696971]">
            You got{" "}
            <span className="font-bold">
              {quantity} {parseInt(quantity) <= 1 ? "chance" : "chances"}
            </span>{" "}
            to win an
            <img
              src={"/assets/images/winners/instant-win.svg"}
              alt="instantWin"
              className="mx-1 inline"
              width={10}
              height={17}
            />
            <span className="text-[#FF8200]">Instant prize</span>
          </p>
        )}

        <div className="flex self-stretch justify-between items-center mt-4 mb-2 text-secondary font-jost text-sm font-medium leading-5 lg:text-base lg:leading-6">
          <span>Total:</span>
          <span className="text-dark-1">
            {numberFormatter.format(amountDue)}{" "}
            {amountSaved ? <span className={"text-gray-5"}>(Save {numberFormatter.format(amountSaved)})</span> : null}
          </span>
        </div>

        <div className={showInstantWinText ? "" : "mt-2"}>
          {!isUsingSubscriptionTickets && (
            <button
              onClick={addTicketsToBasket}
              className={`theme-btn theme-btn-primary theme-btn-lg uppercase w-full ${styles.addToBasket} btnWithArrow`}>
              <span className="relative">
                ADD TO BASKET{" "}
                <ChevronsRightIcon
                  variant={3}
                  className={"size-6 shrink-0 absolute left-[calc(100%+theme(spacing.2))] inset-y-0 my-auto arrowImage"}
                />
              </span>
            </button>
          )}

          {isUsingSubscriptionTickets && (
            <button
              type="button"
              onClick={handleSubscriptionTicketsEnterNow}
              disabled={isEnterNowInFlight}
              aria-busy={isEnterNowInFlight}
              className={`theme-btn theme-btn-primary theme-btn-lg uppercase w-full ${styles.addToBasket} btnWithArrow`}>
              <span className="relative">
                ENTER NOW{" "}
                <ChevronsRightIcon
                  variant={3}
                  className={"size-6 shrink-0 absolute left-[calc(100%+theme(spacing.2))] inset-y-0 my-auto arrowImage"}
                />
              </span>
            </button>
          )}
        </div>
      </div>

      {showSubscriptionEnterConfirmaiton && (
        <div className="flex flex-col items-center gap-6 pt-2 pb-2">
          <div className="flex flex-col items-center gap-3 w-full">
            <p className="text-[#28282b] text-center font-jost text-lg font-semibold leading-6">
              {"🎉 You're in the draw!"}
            </p>

            <div className="flex items-center gap-3 w-full px-2 py-3">
              <svg
                className="shrink-0"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0"
                  stroke="#308835"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 12l2 2l4 -4"
                  stroke="#308835"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              <div className="flex flex-col gap-1 grow min-w-0">
                <p className="text-[#28282b] font-jost text-sm font-medium leading-5">
                  {subscriptionTicketsEnteredCount}{" "}
                  {subscriptionTicketsEnteredCount === 1 ? "Pass ticket" : "Pass tickets"} entered
                </p>
                <p className="text-[#4c4c52] font-jost text-xs font-normal leading-4 line-clamp-3 break-words">
                  {subscriptionTicketEnteredCompetitionName}
                </p>
              </div>

              <span className="shrink-0 flex items-center justify-center px-3 py-1 rounded-[20px] bg-[#c5ebc7]">
                <span className="text-[#308835] font-jost text-[10px] font-medium leading-3">DONE</span>
              </span>
            </div>
          </div>

          <div className="flex flex-col items-center gap-4 w-full">
            <button
              onClick={handleAddMoreTickets}
              className={`theme-btn theme-btn-primary theme-btn-lg uppercase w-full ${styles.addToBasket} btnWithArrow`}>
              <span className="relative">
                ADD MORE TICKETS{" "}
                <ChevronsRightIcon
                  variant={3}
                  className={"size-6 shrink-0 absolute left-[calc(100%+theme(spacing.2))] inset-y-0 my-auto arrowImage"}
                />
              </span>
            </button>

            {showInstantWinText && (
              <a
                href={subscriptionTicketsButtonUrl}
                className="flex items-center justify-center gap-2 w-full h-12 rounded uppercase border border-solid border-primary text-primary font-jost text-sm font-medium">
                REVEAL RESULTS
                <ChevronsRightIcon variant={4} className={"size-4 shrink-0"} />
              </a>
            )}

            <button
              onClick={goToFeaturedCompetitions}
              className="flex items-center justify-center gap-2 w-full h-12 rounded uppercase text-primary font-jost text-sm font-medium">
              FEATURED COMPETITIONS
              <ChevronsRightIcon variant={4} className={"size-4 shrink-0"} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default BuyTicket;
